import { useEffect, useState } from 'react';
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useBoolean
} from '@chakra-ui/react';
import Swal from 'sweetalert2';
import { IoEye, IoEyeOff } from 'react-icons/io5';

import { Button, Input, Label } from '../../../../common/materials';
import { UserCompany } from '../../../../models/user/UserCompany';
import { ResetPasswordRequest } from '../../../../models/authentication/ResetPasswordRequest';
import { useLoader, useStore } from '../../../../common/hooks';

import { useChangePassword, useF1ValidateChangePassword, useF1ConfirmLoginChangePassword, useF1ChangePassword } from '../queries';
import { PasswordHintTooltip } from '../../../../common/components/PasswordHintTooltip';

import { F1PasswordManagement, GetF1PasswordManagementRequest } from '../../../../models/config/F1PasswordManagement';
import { F1ResetPasswordResponse } from "../../../../models/authentication/F1ResetPasswordResponse";
import axios from 'axios';

export type UserCredentialFormModalProps = {
    isSCG: boolean;
    isOpen: boolean;
    user?: UserCompany;
    requiredOldPassword?: boolean;
    onF1ResetPasswordResponseChange: React.Dispatch<React.SetStateAction<F1ResetPasswordResponse[]>>;
    onClose: () => void;
};

export const UserCredentialFormModal = (props: UserCredentialFormModalProps) => {
    const { isSCG, isOpen, user, requiredOldPassword, onF1ResetPasswordResponseChange, onClose } = props;

    const loader = useLoader();

    const { mutate: changePassword } = useChangePassword();
    const { mutate: f1ValidateChangePassword } = useF1ValidateChangePassword();
    const { mutate: f1ConfirmLoginChangePassword } = useF1ConfirmLoginChangePassword();
    const { mutate: f1ChangePassword } = useF1ChangePassword();

    const { f1PasswordManagementStore } = useStore();

    const [oldPassword, setOldPassword] = useState('');
    const [isShowOldPassword, setIsShowOldPassword] = useBoolean();

    const [newPassword, setNewPassword] = useState('');
    const [isShowNewPassword, setIsShowNewPassword] = useBoolean();

    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isShowConfirmNewPassword, setIsShowConfirmNewPassword] = useBoolean();

    const [f1PasswordManagement, setF1PasswordManagement] = useState<F1PasswordManagement>();
    const [passValidate, setPassValidate] = useState({
        error: '',
        length: false,
        maxLength: false,
        numLower: false,
        numNums: false,
        numSpecials: false,
        numUpper: false,
        result: false,
        username: false
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const dataAppEnv = (await axios.get('/v1/app-env')).data;
                const dataLinkSid = (await axios.get('/v1/link-sid')).data;

                const searchF1PasswordManagement: GetF1PasswordManagementRequest = {
                    sid: dataLinkSid.link_sid === 'SCG' ? "1s41kgyAnv4dfqxdRXzDVIEf7XJ" : "",
                    systemType: dataAppEnv.app_env,
                };

                await f1PasswordManagementStore.fetchF1PasswordManagement(searchF1PasswordManagement);
                const f1PassswordManagementData = f1PasswordManagementStore.f1PassswordManagement;

                setF1PasswordManagement(f1PassswordManagementData[0]);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (isOpen) {
            setNewPassword('');
        }
    }, [isOpen]);

    const extractMessage = (input: string): string | undefined => {
        try {
            const jsonStart = input.indexOf('[{');
            if (jsonStart === -1) return undefined;

            const jsonStr = input.slice(jsonStart); // ตัดเอาเฉพาะส่วน JSON
            const parsed: { message: string }[] = JSON.parse(jsonStr);

            return parsed[0]?.message ?? undefined;
        } catch {
            return undefined;
        }
    };

    const buildCriteriaList = () => {
        const items: { label: string; pass: boolean }[] = [];

        items.push({ label: `ความยาวอย่างน้อย ${f1PasswordManagement?.MINIMUMPWD ?? 0} ตัวอักษร`, pass: passValidate.length });
        items.push({ label: `ความยาวไม่เกิน ${f1PasswordManagement?.PWDLENGTH ?? 0} ตัวอักษร`, pass: passValidate.maxLength });
        items.push({ label: 'ห้ามมี Username ใน Password', pass: passValidate.username });

        if (f1PasswordManagement?.RequireCharacterUpper?.toLowerCase() === 'true')
            items.push({ label: 'ต้องมีตัวพิมพ์ใหญ่ (A-Z)', pass: passValidate.numUpper });
        if (f1PasswordManagement?.RequireCharacterLower?.toLowerCase() === 'true')
            items.push({ label: 'ต้องมีตัวพิมพ์เล็ก (a-z)', pass: passValidate.numLower });
        if (f1PasswordManagement?.RequireNumber?.toLowerCase() === 'true')
            items.push({ label: 'ต้องมีตัวเลข (0-9)', pass: passValidate.numNums });
        if (f1PasswordManagement?.RequireSpecialCharacter?.toLowerCase() === 'true')
            items.push({ label: 'ต้องมีอักขระพิเศษ (!@#$%^&*)', pass: passValidate.numSpecials });

        items.push({ label: 'Password ใหม่ตรงกับ Confirm Password', pass: newPassword === confirmNewPassword && newPassword.length > 0 });

        if (requiredOldPassword) {
            items.push({ label: 'กรอก Current Password', pass: oldPassword.length > 0 });
            items.push({ label: 'Password ใหม่ต้องไม่ซ้ำกับ Current Password', pass: newPassword !== oldPassword || newPassword.length === 0 });
        }

        return items;
    };

    const handleChangePasswordClick = async () => {
        if (!user) {
            return;
        }

        const criteria = buildCriteriaList();
        const allPass = criteria.every(c => c.pass);

        if (!allPass) {
            const html = criteria
                .map(c =>
                    `<div style="text-align:left;padding:4px 0;color:${c.pass ? '#16a34a' : '#dc2626'}">` +
                    `${c.pass ? '✓' : '✗'} ${c.label}</div>`
                )
                .join('');

            await Swal.fire({
                title: 'Password Criteria',
                html,
                icon: 'warning',
                confirmButtonText: 'ตกลง'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Confirm Change Password?',
            text: 'You will not be able to revert this!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, change it!'
        });
        if (!result.isConfirmed) {
            return;
        }

        loader.show();

        const request: ResetPasswordRequest = {
            old_password: oldPassword,
            password: newPassword,
            re_password: newPassword,
            username: user.citizen_id,
            email: user.email,
            changed_by_admin: true
        };
        if (isSCG) {
            f1ValidateChangePassword(request, {
                onSuccess: async (response) => {
                    console.log('f1ValidateChangePassword', response)
                    //await Swal.fire('Success!', 'Password has been changed.', 'success');
                    //onF1ResetPasswordResponseChange(response);

                    const filterError = response.filter(item => item.status === 'error');
                    if (filterError && filterError.length > 0) {
                        await Swal.fire('Error!', extractMessage(filterError[0].message.replace(/\n/g, "<br>")), 'error');

                        loader.hide();
                    } else {
                        if (requiredOldPassword) {
                            f1ConfirmLoginChangePassword(request, {
                                onSuccess: async (response) => {
                                    console.log('f1ConfirmLoginChangePassword', response)
                                    //await Swal.fire('Success!', 'Password has been changed.', 'success');
                                    //onF1ResetPasswordResponseChange(response);

                                    const filterSuccess = response.filter(item => item.status === 'success');
                                    if (filterSuccess && filterSuccess.length > 0) {
                                        f1ChangePassword(request, {
                                            onSuccess: async (response) => {
                                                //console.log(response)
                                                //await Swal.fire('Success!', 'Password has been changed.', 'success');
                                                onF1ResetPasswordResponseChange(response);

                                                setConfirmNewPassword('');
                                                setNewPassword('');
                                                setOldPassword('');

                                                loader.hide();
                                                onClose();
                                            },
                                            onError: async (error) => {
                                                console.log(error)
                                                await Swal.fire('Error!', 'Something went wrong.', 'error');

                                                loader.hide();
                                            },
                                            onSettled: () => {
                                                loader.hide();
                                                //onClose();
                                            }
                                        });
                                        //loader.hide();
                                    } else {
                                        await Swal.fire('Error!', 'Current Password ผิดพลาด กรุณาตรวจสอบ', 'error');

                                        loader.hide();
                                        //onClose();
                                    }
                                },
                                onError: async (error) => {
                                    console.log(error)
                                    await Swal.fire('Error!', 'Something went wrong.', 'error');

                                    loader.hide();
                                    //onClose();
                                },
                                onSettled: () => {
                                    //loader.hide();
                                    //onClose();
                                }
                            });
                        }
                        else
                        {
                            f1ChangePassword(request, {
                                onSuccess: async (response) => {
                                    //console.log(response)
                                    //await Swal.fire('Success!', 'Password has been changed.', 'success');
                                    onF1ResetPasswordResponseChange(response);

                                    setConfirmNewPassword('');
                                    setNewPassword('');
                                    setOldPassword('');

                                    loader.hide();
                                    onClose();
                                },
                                onError: async (error) => {
                                    console.log(error)
                                    await Swal.fire('Error!', 'Something went wrong.', 'error');

                                    loader.hide();
                                },
                                onSettled: () => {
                                    loader.hide();
                                    //onClose();
                                }
                            });
                        }
                    }
                },
                onError: async (error) => {
                    console.log(error)
                    await Swal.fire('Error!', 'Something went wrong.', 'error');

                    loader.hide();
                    //onClose();
                },
                onSettled: () => {
                    //loader.hide();
                    //onClose();
                }
            });
            //f1ChangePassword(request, {
            //    onSuccess: async (response) => {
            //        //console.log(response)
            //        //await Swal.fire('Success!', 'Password has been changed.', 'success');
            //        onF1ResetPasswordResponseChange(response);
            //    },
            //    onError: async (error) => {
            //        console.log(error)
            //        await Swal.fire('Error!', 'Something went wrong.', 'error');
            //    },
            //    onSettled: () => {
            //        loader.hide();
            //        onClose();
            //    }
            //});
        }
        else {
            changePassword(request, {
                onSuccess: async () => {
                    await Swal.fire('Success!', 'Password has been changed.', 'success');
                },
                onError: async () => {
                    await Swal.fire('Error!', 'Something went wrong.', 'error');
                },
                onSettled: () => {
                    loader.hide();
                    onClose();
                }
            });
        }
    };

    const isOkPass = (p) => {
        var anUpperCase = /[A-Z]/;
        var aLowerCase = /[a-z]/;
        var aNumber = /[0-9]/;
        var aSpecial = /[!|@|#|$|%|^|&|*|(|)|-|_]/;
        var obj: any = {};
        obj.result = false;

        if (p.length < parseInt(f1PasswordManagement?.MINIMUMPWD ?? '0')) {
            obj.length = false;
            obj.error = 'Not long enough!';
        } else {
            obj.length = true;
        }

        if (p.length > parseInt(f1PasswordManagement?.PWDLENGTH ?? '0')) {
            obj.maxLength = false;
            obj.error = 'Too long!';
        } else {
            obj.maxLength = true;
        }

        if (p.length > 0) {
            if (p.toLowerCase().includes(user?.citizen_id?.toLocaleLowerCase())) {
                obj.username = false;
            } else {
                obj.username = true;
            }
        } else {
            obj.username = true;
        }

        var numUpper = 0;
        var numLower = 0;
        var numNums = 0;
        var numSpecials = 0;
        for (var i = 0; i < p.length; i++) {
            if (anUpperCase.test(p[i])) numUpper++;
            else if (aLowerCase.test(p[i])) numLower++;
            else if (aNumber.test(p[i])) numNums++;
            else if (aSpecial.test(p[i])) numSpecials++;
        }

        if (f1PasswordManagement?.RequireCharacterUpper?.toLowerCase() === 'true' && numUpper < 1) {
            obj.result = false;
            obj.error = 'Wrong Format!';
            obj.numUpper = false;
        } else {
            obj.numUpper = true;
        }

        if (f1PasswordManagement?.RequireCharacterLower?.toLowerCase() === 'true' && numLower < 1) {
            obj.result = false;
            obj.error = 'Wrong Format!';
            obj.numLower = false;
        } else {
            obj.numLower = true;
        }

        if (f1PasswordManagement?.RequireNumber?.toLowerCase() === 'true' && numNums < 1) {
            obj.result = false;
            obj.error = 'Wrong Format!';
            obj.numNums = false;
        } else {
            obj.numNums = true;
        }

        if (f1PasswordManagement?.RequireSpecialCharacter?.toLowerCase() === 'true' && numSpecials < 1) {
            obj.result = false;
            obj.error = 'Wrong Format!';
            obj.numSpecials = false;
        } else {
            obj.numSpecials = true;
        }

        if (
            obj.numUpper &&
            obj.numLower &&
            obj.numNums &&
            obj.numSpecials &&
            obj.length &&
            obj.maxLength &&
            obj.username
        ) {
            obj.result = true;
        } else {
            obj.result = false;
        }

        return obj;
    }

    const OldPasswordEyeIcon = isShowOldPassword ? IoEyeOff : IoEye;
    const NewPasswordEyeIcon = isShowNewPassword ? IoEyeOff : IoEye;
    const ConfirmNewPasswordEyeIcon = isShowConfirmNewPassword ? IoEyeOff : IoEye;

    return (
        <Modal
            size="2xl"
            isOpen={isOpen}
            onClose={onClose}
            scrollBehavior="outside"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Change Password</ModalHeader>
                <ModalCloseButton />
                <ModalBody className="flex flex-col gap-4 overflow-visible">
                    <div>
                        <Label>Username</Label>
                        <Input
                            disabled
                            defaultValue={user?.citizen_id}
                            className="w-full rounded border border-gray-300 px-2 py-1 disabled:border-[#e6e6e6] disabled:bg-[#f2f2f2]"
                        />
                    </div>
                    {requiredOldPassword && (
                        <div>
                            <Label>Current Password</Label>
                            <div className="relative">
                                <Input
                                    autoComplete="one-time-code"
                                    type={isShowOldPassword ? 'text' : 'password'}
                                    value={oldPassword}
                                    onChange={event => {
                                        setOldPassword(event.target.value)
                                    }}
                                    className="w-full rounded border border-gray-300 px-2 py-1 disabled:border-[#e6e6e6] disabled:bg-[#f2f2f2]"
                                />
                                <OldPasswordEyeIcon
                                    className="absolute right-2 top-2 cursor-pointer text-neutral-600"
                                    onClick={setIsShowOldPassword.toggle}
                                />
                            </div>
                        </div>
                    )}
                    <div>
                        <Label>New Password</Label>
                        <div className="relative">
                            <Input
                                autoComplete="one-time-code"
                                type={isShowNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={event => {
                                    const ans = isOkPass(event.target.value)
                                    setPassValidate(ans)
                                    setNewPassword(event.target.value)
                                }}
                                className="w-full rounded border border-gray-300 px-2 py-1 disabled:border-[#e6e6e6] disabled:bg-[#f2f2f2]"
                                data-tooltip-place="left"
                                data-tooltip-id="global"
                            />
                            <NewPasswordEyeIcon
                                className="absolute right-2 top-2 cursor-pointer text-neutral-600"
                                onClick={setIsShowNewPassword.toggle}
                            />
                            <PasswordHintTooltip
                                passwordValidate={passValidate}
                                config={f1PasswordManagement}
                            />
                        </div>
                        {(requiredOldPassword && (newPassword === oldPassword) && (newPassword.length > 0) && (oldPassword.length > 0)) && (
                            <p className='text-sm text-primary-900'>New password same as Current password.</p>
                        )}
                    </div>
                    <div>
                        <Label>Confirm New Password</Label>
                        <div className="relative">
                            <Input
                                autoComplete="one-time-code"
                                type={isShowConfirmNewPassword ? 'text' : 'password'}
                                value={confirmNewPassword}
                                onChange={event => setConfirmNewPassword(event.target.value)}
                                className="w-full rounded border border-gray-300 px-2 py-1 disabled:border-[#e6e6e6] disabled:bg-[#f2f2f2]"
                            />
                            <ConfirmNewPasswordEyeIcon
                                className="absolute right-2 top-2 cursor-pointer text-neutral-600"
                                onClick={setIsShowConfirmNewPassword.toggle}
                            />
                        </div>
                        {((newPassword !== confirmNewPassword) && (confirmNewPassword.length > 0)) && (
                            <p className='text-sm text-primary-900'>New password and confirm password do not match.</p>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter className="gap-4">
                    <Button
                        outline
                        size="sm"
                        className="text-gray-400"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleChangePasswordClick}
                        className="bg-primary-900 text-white"
                    >
                        Change Password
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
